import React, { useState } from 'react';
import { Braces, Copy, Trash2, ArrowRight } from 'lucide-react';

const ObjectToJsonTool = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const convertToJson = () => {
    setError('');
    setOutput('');

    if (!input.trim()) {
      setError('Please enter JavaScript object notation');
      return;
    }

    try {
      // Try to parse as JSON first (already valid JSON)
      try {
        const parsed = JSON.parse(input);
        setOutput(JSON.stringify(parsed, null, 2));
        return;
      } catch {}

      // Convert JS object literal to JSON
      let processed = input.trim();

      // Remove trailing semicolons and var/let/const declarations
      processed = processed.replace(/^(var|let|const)\s+\w+\s*=\s*/, '');
      processed = processed.replace(/;$/, '');

      // Handle unquoted keys
      processed = processed.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:/g, '$1"$2":');

      // Handle single quotes to double quotes (but not inside strings)
      let result = '';
      let inString = false;
      let stringChar = null;
      
      for (let i = 0; i < processed.length; i++) {
        const char = processed[i];
        const prevChar = processed[i - 1];
        
        if (!inString && (char === '"' || char === "'")) {
          inString = true;
          stringChar = char;
          result += '"';
        } else if (inString && char === stringChar && prevChar !== '\\') {
          inString = false;
          stringChar = null;
          result += '"';
        } else {
          result += char;
        }
      }

      // Handle trailing commas
      result = result.replace(/,(\s*[}\]])/g, '$1');

      // Handle undefined and functions
      result = result.replace(/:\s*undefined/g, ': null');

      // Try to parse the result
      const parsed = JSON.parse(result);
      setOutput(JSON.stringify(parsed, null, 2));
    } catch (e) {
      setError(`Conversion failed: ${e.message}. Make sure input is valid JavaScript object notation.`);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadSample = () => {
    const sample = `{
  name: 'John Doe',
  age: 30,
  isActive: true,
  address: {
    city: 'New York',
    country: 'USA',
  },
  hobbies: ['reading', 'gaming', 'coding'],
  metadata: undefined,
}`;
    setInput(sample);
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
        <p className="text-sm text-blue-300">
          Convert JavaScript object literals to valid JSON. Handles unquoted keys, single quotes, 
          trailing commas, and other JS-specific syntax.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-gray-300">JavaScript Object</label>
            <button
              onClick={loadSample}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Load Sample
            </button>
          </div>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="{ name: 'John', age: 30 }"
            className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">JSON Output</label>
          <div className="w-full h-64 bg-gray-800 border border-gray-700 rounded-lg p-4 overflow-auto">
            {error ? (
              <div className="text-red-400 text-sm">{error}</div>
            ) : output ? (
              <pre className="text-green-400 text-sm whitespace-pre-wrap">{output}</pre>
            ) : (
              <span className="text-gray-500 text-sm">Valid JSON will appear here...</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={convertToJson}
          disabled={!input.trim()}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
        >
          <ArrowRight size={18} />
          Convert to JSON
        </button>
        <button
          onClick={copyToClipboard}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <button
          onClick={() => { setInput(''); setOutput(''); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">What Gets Converted</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-400 mb-2">JavaScript (Input)</div>
            <ul className="space-y-1 text-gray-500">
              <li><code className="text-yellow-400">name:</code> → unquoted keys</li>
              <li><code className="text-yellow-400">'value'</code> → single quotes</li>
              <li><code className="text-yellow-400">,]</code> → trailing commas</li>
              <li><code className="text-yellow-400">undefined</code> → undefined values</li>
            </ul>
          </div>
          <div>
            <div className="text-gray-400 mb-2">JSON (Output)</div>
            <ul className="space-y-1 text-gray-500">
              <li><code className="text-green-400">"name":</code> → quoted keys</li>
              <li><code className="text-green-400">"value"</code> → double quotes</li>
              <li><code className="text-green-400">]</code> → no trailing commas</li>
              <li><code className="text-green-400">null</code> → null values</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObjectToJsonTool;
